import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Redirect,
  Route,
  useHistory,
  useLocation,
  useRouteMatch
} from 'react-router-dom';
import FirstScreen from 'routes/App/byocOnboarding/FirstScreen';
import SignUp from 'routes/App/byocOnboarding/SignUp';
import SignUpSuccess from 'routes/App/byocOnboarding/SignUp/SignUpSuccess';
import MenuSetup from 'routes/App/byocOnboarding/MenuSetup';
import OrderSourceIntegration from 'routes/App/byocOnboarding/OrderSourceIntegration';
import PaymentDetails from 'routes/App/byocOnboarding/PaymentDetails';
import IntegrationStatus from 'routes/App/byocOnboarding/IntegrationStatus';
import {
  CLEAR_BYOC_ONBOARDING_DATA,
  GET_BYOC_ONBOARDING_DATA
} from 'store/byocOnboarding/actions';
import useBlockBackNavigation from 'routes/App/byocOnboarding/hooks/useBlockBackNavigation';
import FullScreenLoading from 'components/shared/Utils/FullScreenLoading/FullScreenLoading';

const ByocOnboardingRoutes = () => {
  const match = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const {lastStep, loading, data, error} = useSelector(
    (state) => state.byocOnboardingReducer || {}
  );
  const {activeLocation} = useSelector((state) => state.user);

  // Define which pages have Back button
  // Pages WITHOUT Back button: start, sign-up-success, integration-status
  // Pages WITH Back button: sign-up, menu-setup, order-source-integration, payment-details
  const pagesWithBackButton = [
    'sign-up',
    'menu-setup',
    'order-source-integration',
    'payment-details'
  ];

  // Check if current page has Back button
  const hasBackButton = pagesWithBackButton.some((page) =>
    location.pathname.includes(page)
  );

  // Block back navigation if no Back button
  useBlockBackNavigation(hasBackButton);

  useEffect(() => {
    // Fetch onboarding data on mount
    if (activeLocation?._id) {
      dispatch(GET_BYOC_ONBOARDING_DATA());
    }
    return () => {
      dispatch(CLEAR_BYOC_ONBOARDING_DATA());
    };
  }, [activeLocation?._id]);

  // If there's an error fetching data, redirect to start page
  useEffect(() => {
    if (error && !loading) {
      const currentPath = history?.location?.pathname;
      if (currentPath && !currentPath.includes('/start')) {
        history.push('/byoc-onboarding/start');
      }
    }
  }, [error, loading, history]);

  // Show loading screen while data is being fetched
  if (loading && !data) {
    return <FullScreenLoading />;
  }

  useEffect(() => {
    if (loading) return;
    if (error && lastStep === 'start') {
      history.push('/byoc-onboarding/start');
      return;
    }
    if (!data?.signup?.isFinished && lastStep === 'start') {
      history.push('/byoc-onboarding/start');
      return;
    }

    const currentPath = history?.location?.pathname;
    const isOnboardingRoute = currentPath?.startsWith('/byoc-onboarding');

    if (!isOnboardingRoute || !lastStep) return;

    // Map of paths to their step names
    const pathToStepMap = {
      '/byoc-onboarding/start': 'start',
      '/byoc-onboarding/sign-up': 'sign-up',
      '/byoc-onboarding/sign-up-success': 'sign-up-success',
      '/byoc-onboarding/menu-setup': 'menu-setup',
      '/byoc-onboarding/order-source-integration': 'order-source-integration',
      '/byoc-onboarding/payment-details': 'payment-details',
      '/byoc-onboarding/integration-status': 'integration-status'
    };

    const currentStep = pathToStepMap[currentPath];

    // If we're on the base route, redirect to last step
    if (
      currentPath === '/byoc-onboarding' ||
      currentPath === '/byoc-onboarding/'
    ) {
      if (lastStep && lastStep !== 'start') {
        history.push(`/byoc-onboarding/${lastStep}`);
      } else {
        history.push('/byoc-onboarding/start');
      }
      return;
    }

    // If we're on a specific step, check if we should redirect
    if (currentStep) {
      // Check if user should have access to payment-details even if lastStep is integration-status
      const paymentDetails = data?.paymentDetails || data?.PaymentDetails || {};
      const isPaymentMethodNotConnected =
        paymentDetails?.paymentMethodConnected === false ||
        paymentDetails?.paymentMethodConnected === undefined;
      const isOnPaymentDetails = currentStep === 'payment-details';
      const orderSourceData =
        data?.orderSourceIntegration || data?.OrderSourceIntegration || {};
      const isOrderSourceFinished =
        orderSourceData?.isFinished === true ||
        orderSourceData?.IsFinished === true;

      // Allow access to payment-details if OrderSourceIntegration is finished
      // and payment method is not connected (even if PaymentDetails.IsFinished = true)
      const shouldAllowPaymentDetailsAccess =
        isOnPaymentDetails &&
        isOrderSourceFinished &&
        isPaymentMethodNotConnected;

      // If user should have access to payment-details, don't redirect
      if (shouldAllowPaymentDetailsAccess) {
        return; // Allow access to payment-details
      }

      const stepOrder = [
        'start',
        'sign-up',
        'sign-up-success',
        'menu-setup',
        'order-source-integration',
        'payment-details',
        'integration-status'
      ];

      const currentStepIndex = stepOrder.indexOf(currentStep);
      const lastStepIndex = stepOrder.indexOf(lastStep);

      // If current step is before the last step, redirect to last step

      history.push(`/byoc-onboarding/${lastStep}`);
    }
  }, [history, lastStep, loading, error, data]);

  return (
    <>
      <Route exact path={match.path}>
        <Redirect to={`${match.path}/start`} />
      </Route>
      <Route path={`${match.path}/start`} render={() => <FirstScreen />} />
      <Route path={`${match.path}/sign-up`} render={() => <SignUp />} />
      <Route
        path={`${match.path}/sign-up-success`}
        render={() => <SignUpSuccess />}
      />
      <Route path={`${match.path}/menu-setup`} render={() => <MenuSetup />} />
      <Route
        path={`${match.path}/order-source-integration`}
        render={() => <OrderSourceIntegration />}
      />
      <Route
        path={`${match.path}/payment-details`}
        render={() => <PaymentDetails />}
      />
      <Route
        path={`${match.path}/integration-status`}
        render={() => <IntegrationStatus />}
      />
    </>
  );
};

export default ByocOnboardingRoutes;
